import { useCallback } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { useField } from 'react-final-form';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { KnmAddressField } from 'KnmPackage/MainModule/components/KnmAddressField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { Button } from 'UIPackage/MainModule/components/Button';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { IDictionaryDangerClassSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryDangerClassSelectOption';
import { IDictionaryRiskCategorySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryRiskCategorySelectOption';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';

import styles from './KnmErpObject.module.scss';
import { FieldReplaceAlert } from './FieldReplaceAlert';
import { IDictionarySupervisionTypeSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IKnmErpFormResult } from '../../KnmErpResultsModule/interfaces/IKnmErpFormResult';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import {
  getKnmErpDangerClassesSelectOptions,
  getKnmErpRiskCategoriesSelectOptions,
  getKnmErpSubjectTypesSelectOptions,
  getKnmErpSupervisionTypesSelectOptions,
  getKnmFormRef,
} from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { getDecisionKnoSupervisionHazardClassApplies } from '../../../DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { ISubjectType } from '../../../APIPackage/DictionariesModule/interfaces/ISubjectType';
import { IFederalLawErvk } from '../../../APIPackage/CatalogsModule/interfaces/IFederalLawErvk';
import { getUserRoleIsProsecutorOrSuperProsecutor } from '../../../AuthPackage/MainModule/selectors/authSelectors';

const COLLAPSE_TITLE_BASE = 'Объект';

const excludedFederalLaws = ['414', '131', '28-ФЗ', '68-ФЗ'];

interface IKnmErpObjectErvkProps {
  address: string | undefined;
  autocomplete: boolean | undefined;
  id: string;
  index: number;
  isNew: boolean | undefined;
  name: string;
  objectGuid: string;
  onRemove: () => void;
  registryLegalAddressFns?: string;
  replaceAlertsVisible?: boolean;
  watchOnly?: boolean;
}

interface IKnmErpObjectErvkStoreProps {
  dangerClassesSelectOptionsErvk: IDictionaryDangerClassSelectOption[] | undefined;
  formRef: IKnmFormRef | undefined;
  hazardClassApplies: boolean | undefined;
  isProsecutorOrSuperProsecutor: boolean | undefined;
  knmErpSupervisionTypesSelectOptions: IDictionarySupervisionTypeSelectOption[] | undefined;
  objectTypesSelectOptions: IDictionarySelectOption<ISubjectType>[] | undefined;
  riskCategoriesSelectOptionsErvk: IDictionaryRiskCategorySelectOption[] | undefined;
}

export const KnmErpObjectErvkWithoutStore = ({
  address,
  autocomplete,
  id,
  index,
  isNew,
  name,
  objectGuid,
  onRemove,
  registryLegalAddressFns,
  replaceAlertsVisible,
  watchOnly,
  dangerClassesSelectOptionsErvk,
  formRef,
  hazardClassApplies,
  isProsecutorOrSuperProsecutor,
  knmErpSupervisionTypesSelectOptions,
  objectTypesSelectOptions,
  riskCategoriesSelectOptionsErvk,
}: IKnmErpObjectErvkProps & IKnmErpObjectErvkStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const makeAddSupervisionHandler = (fieldName: string) => () =>
    !watchOnly && push && push(fieldName, { listId: uuidv4(), supervision: undefined });
  const makeRemoveSupervisionHandler = (fieldName: string, i: number) => () =>
    !watchOnly && remove && remove(fieldName, i);

  const isAddressFieldAlertVisible = Boolean(
    !isNew && replaceAlertsVisible && registryLegalAddressFns && address !== registryLegalAddressFns,
  );

  const federalLaw: IFederalLawErvk | undefined = formRef?.current.getFieldState('federalLaw')?.value;
  const shouldValidateObjectType =
    !isProsecutorOrSuperProsecutor && federalLaw?.number && !excludedFederalLaws.includes(federalLaw.number);

  const isAddressHazardValue = useField(`${name}.isAddressHazard`, { subscription: { value: true } }).input.value;

  const addressFieldName = `${name}.address` as keyof IKnmForm;
  const autocompleteFieldName = `${name}.autocomplete` as keyof IKnmForm;

  const handleAddressFieldBlur = useCallback(
    (addressValue: string) => formRef?.current.change(addressFieldName, addressValue),
    [addressFieldName, formRef],
  );

  const handleAddressReplaceButtonClick = useCallback(() => {
    formRef?.current.change(addressFieldName, registryLegalAddressFns);
  }, [addressFieldName, formRef, registryLegalAddressFns]);

  const handleAutocompleteClear = useCallback(() => {
    formRef?.current.change('organizationAutocomplete', false);
    formRef?.current.change(autocompleteFieldName, false);
  }, [autocompleteFieldName, formRef]);

  const changeIsAddressHazard = () => {
    const objectResults: IKnmErpFormResult[] | undefined = formRef?.current.getFieldState('objectsResults')?.value;

    objectResults?.forEach((result, objectResultIndex) => {
      const resultObjectId = result.object?.guid;

      if (resultObjectId && resultObjectId === objectGuid) {
        const refreshObjectResult: IKnmErpFormResult = {
          ...result,
          isAddressHazard: !isAddressHazardValue,
        };

        formRef?.current.change(`objectsResults[${objectResultIndex}]` as keyof IKnmForm, refreshObjectResult);
      }
    });

    formRef?.current.change(`${name}.isAddressHazard` as keyof IKnmForm, !isAddressHazardValue);
  };

  return (
    <KnmCollapse
      id={id}
      theme="fields"
      title={isNew ? COLLAPSE_TITLE_BASE : `${COLLAPSE_TITLE_BASE} №${index + 1}`}
      subtitle={address}
      isNew={isNew}
      openByDefault={isNew}
      onClose={onRemove}
    >
      <>
        <Row>
          <FieldBlock label="Местонахождение" placementLabel="left" color="black" labelSize="col-4">
            <KnmAddressField
              className={styles.erpKnmAdressField}
              resize="none"
              formRef={formRef}
              prefixNameNotInFias={`${name}.`}
              prefixNameMatchesObjectAddress={`${name}.`}
              manualAddressPlaceholder="Место"
              id={addressFieldName}
              addressFieldPathWithPrefix={addressFieldName}
              required
              type="row-field"
              withWarning={isAddressFieldAlertVisible}
              clearable={autocomplete}
              disabled={watchOnly || autocomplete}
              onAddressFieldBlur={handleAddressFieldBlur}
              onClear={handleAutocompleteClear}
            />
            {isAddressFieldAlertVisible && (
              <FieldReplaceAlert
                className={styles.FieldReplaceAlert}
                text={`Значение ЕГРЮЛ/ЕГРИП: ${registryLegalAddressFns}`}
                onReplaceButtonClick={handleAddressReplaceButtonClick}
              />
            )}
            <TextareaField className={styles.AddressField} name={addressFieldName} />
          </FieldBlock>
        </Row>

        <Row>
          <FieldBlock>
            <CheckboxField
              className={styles.isAddressHazard}
              theme="system"
              size="medium"
              type="checkbox"
              id={`${name}.isAddressHazard`}
              name={`${name}.isAddressHazard`}
              disabled={watchOnly}
              label={'Является ОПО'}
              onChange={changeIsAddressHazard}
            />
          </FieldBlock>
        </Row>

        <Row>
          <FieldBlock label="Тип объекта" placementLabel="left" color="black" labelSize="col-4" id={`${id}.objectType`}>
            <SelectInputField
              className="shared-row-field"
              name={`${name}.objectType`}
              options={objectTypesSelectOptions}
              size="medium"
              validate={
                shouldValidateObjectType
                  ? composeValidators(makeRequired('Поле не может быть пустым'))
                  : () => undefined
              }
            />
          </FieldBlock>
        </Row>

        <Row>
          <FieldBlock
            label={hazardClassApplies ? 'Класс опасности' : 'Категория риска'}
            placementLabel="left"
            color="black"
            labelSize="col-4"
          >
            <SelectInputField
              className="shared-row-field"
              id={`${name}.riskCategory`}
              name={`${name}.riskCategory`}
              options={hazardClassApplies ? dangerClassesSelectOptionsErvk : riskCategoriesSelectOptionsErvk}
              disabled={watchOnly}
              size="medium"
            />
          </FieldBlock>
        </Row>

        <FieldBlock
          label="Вид (виды) контроля (надзора)"
          button={
            <Button
              className="shared-btn-add"
              text="Добавить"
              variant="link"
              color="primary"
              type="button"
              disabled={watchOnly}
              onClick={makeAddSupervisionHandler(`${name}.supervisions`)}
            />
          }
        >
          <FieldArray name={`${name}.supervisions`}>
            {({ fields }) => {
              if (!fields.length) {
                return null;
              }

              return fields.map((supervisionsFieldName, sIndex) => (
                <Row key={supervisionsFieldName}>
                  <SelectInputField
                    className="shared-deleting-row"
                    name={`${supervisionsFieldName}.supervision`}
                    options={knmErpSupervisionTypesSelectOptions}
                    isSearchable
                    disabled={watchOnly}
                    size="medium"
                  />
                  <CloseButton
                    className="shared-deleting-row-btn"
                    onClick={makeRemoveSupervisionHandler(`${name}.supervisions`, sIndex)}
                  />
                </Row>
              ));
            }}
          </FieldArray>
        </FieldBlock>
      </>
    </KnmCollapse>
  );
};

const mapStateToProps = (state: IRootState): IKnmErpObjectErvkStoreProps => ({
  dangerClassesSelectOptionsErvk: getKnmErpDangerClassesSelectOptions(state),
  formRef: getKnmFormRef(state),
  hazardClassApplies: getDecisionKnoSupervisionHazardClassApplies(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  knmErpSupervisionTypesSelectOptions: getKnmErpSupervisionTypesSelectOptions(state),
  objectTypesSelectOptions: getKnmErpSubjectTypesSelectOptions(state),
  riskCategoriesSelectOptionsErvk: getKnmErpRiskCategoriesSelectOptions(state),
});

export const KnmErpObjectErvk = connect(mapStateToProps)(KnmErpObjectErvkWithoutStore);
